// src/api/groupsApi.js
import { apiClient } from './photoProcessingApi';

export const groupsAPI = {
  // ── Groups ────────────────────────────────────────────────────────────────

  /** GET /api/groups/groups/ — all discoverable groups */
  getAllGroups: async (params = {}) => {
    const response = await apiClient.get('/groups/groups/', { params });
    return response.data;
  },

  /** GET /api/groups/groups/my/ — groups the current user belongs to */
  getMyGroups: async () => {
    const response = await apiClient.get('/groups/groups/my/');
    return response.data;
  },

  /** GET /api/groups/groups/{id}/ — full group detail */
  getGroup: async (groupId) => {
    const response = await apiClient.get(`/groups/groups/${groupId}/`);
    return response.data;
  },

  /** POST /api/groups/groups/ — create group (with optional initial_members) */
  // src/api/groupsApi.js  — only createGroup changes

  createGroup: async (data) => {
    const hasFile = data.avatar instanceof File;

    if (hasFile) {
      // Must use FormData to upload the file.
      // initial_members must be JSON-stringified since FormData can't carry nested objects.
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'avatar') {
          formData.append(key, value); // File object
        } else if (key === 'initial_members') {
          // Stringify so the backend to_internal_value() can parse it back
          formData.append(key, JSON.stringify(value));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      const response = await apiClient.post('/groups/groups/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } else {
      // No file — send as JSON. DRF will parse initial_members natively as a list of dicts.
      const payload = { ...data };
      delete payload.avatar; // don't send null/undefined avatar
      const response = await apiClient.post('/groups/groups/', payload);
      return response.data;
    }
  },


  /** PATCH /api/groups/groups/{id}/ — update group settings */
  updateGroup: async (groupId, data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'avatar' && value instanceof File) {
        formData.append(key, value);
      } else if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    const response = await apiClient.patch(`/groups/groups/${groupId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /** DELETE /api/groups/groups/{id}/ */
  deleteGroup: async (groupId) => {
    await apiClient.delete(`/groups/groups/${groupId}/`);
  },

  /** POST /api/groups/groups/{id}/join/ */
  joinGroup: async (groupId) => {
    const response = await apiClient.post(`/groups/groups/${groupId}/join/`);
    return response.data;
  },

  /** POST /api/groups/groups/{id}/approve_join/ */
  approveJoin: async (groupId, userId) => {
    const response = await apiClient.post(`/groups/groups/${groupId}/approve_join/`, {
      user_id: userId,
    });
    return response.data;
  },

  /** POST /api/groups/groups/{id}/decline_join/ */
  declineJoin: async (groupId, userId) => {
    const response = await apiClient.post(`/groups/groups/${groupId}/decline_join/`, {
      user_id: userId,
    });
    return response.data;
  },

  /** GET /api/groups/groups/{id}/pending_requests/ */
  getPendingRequests: async (groupId) => {
    const response = await apiClient.get(`/groups/groups/${groupId}/pending_requests/`);
    return response.data;
  },

  // ── Members ───────────────────────────────────────────────────────────────

  /** GET /api/groups/groups/{id}/members/ */
  getMembers: async (groupId, params = {}) => {
    const response = await apiClient.get(`/groups/groups/${groupId}/members/`, { params });
    return response.data;
  },

  /** POST /api/groups/groups/{id}/invite/ */
  inviteUsers: async (groupId, { user_ids, role, message, expires_in_days }) => {
    const response = await apiClient.post(`/groups/groups/${groupId}/invite/`, {
      user_ids,
      role,
      message,
      expires_in_days,
    });
    return response.data;
  },

  /** GET /api/groups/groups/{id}/invitations/ (admin+) */
  getInvitations: async (groupId) => {
    const response = await apiClient.get(`/groups/groups/${groupId}/invitations/`);
    return response.data;
  },

  /** POST /api/groups/groups/{id}/remove_member/ */
  removeMember: async (groupId, userId) => {
    const response = await apiClient.post(`/groups/groups/${groupId}/remove_member/`, {
      user_id: userId,
    });
    return response.data;
  },

  /** POST /api/groups/groups/{id}/change_role/ */
  changeRole: async (groupId, userId, role) => {
    const response = await apiClient.post(`/groups/groups/${groupId}/change_role/`, {
      user_id: userId,
      role,
    });
    return response.data;
  },

  /** POST /api/groups/groups/{id}/leave/ */
  leaveGroup: async (groupId) => {
    const response = await apiClient.post(`/groups/groups/${groupId}/leave/`);
    return response.data;
  },

  // ── Shared resources ──────────────────────────────────────────────────────

  /** GET /api/groups/groups/{id}/shared_resources/ */
  getSharedResources: async (groupId, params = {}) => {
    const response = await apiClient.get(`/groups/groups/${groupId}/shared_resources/`, { params });
    return response.data;
  },

  /** DELETE /api/groups/shared-resources/{id}/ */
  unshareResource: async (sharedResourceId) => {
    await apiClient.delete(`/groups/shared-resources/${sharedResourceId}/`);
  },

  /** POST /api/groups/shared-resources/{id}/change_permission/ */
  changeResourcePermission: async (sharedResourceId, permissionLevel) => {
    const response = await apiClient.post(
      `/groups/shared-resources/${sharedResourceId}/change_permission/`,
      { permission_level: permissionLevel }
    );
    return response.data;
  },

  // src/api/groupsApi.js - Update shareMultipleResources function

  /** POST /api/groups/groups/{id}/share_multiple/ — share ALL resources of a type */
  shareMultipleResources: async (groupId, {
    resource_types,
    permission_level,
    enable_auto_share = false,
    auto_share_permission = null,
    auto_share_resource_types = null
  }) => {
    const payload = {
      resource_types,
      permission_level,
    };

    if (enable_auto_share) {
      payload.enable_auto_share = true;
      payload.auto_share_permission = auto_share_permission || permission_level;
      payload.auto_share_resource_types = auto_share_resource_types || resource_types;
    }

    const response = await apiClient.post(
      `/groups/groups/${groupId}/share_multiple/`,
      payload
    );
    return response.data;
  },

  // ── Auto-share management ──────────────────────────────────────────────────

  /** POST /api/groups/groups/{id}/auto_share_settings/ — enable/disable auto-sharing */
  manageAutoShare: async (groupId, { enable, permission_level, resource_types }) => {
    const response = await apiClient.post(
      `/groups/groups/${groupId}/auto_share_settings/`,
      {
        enable,
        permission_level,
        resource_types,
      }
    );
    return response.data;
  },

  /** GET /api/groups/groups/{id}/auto_share_status/ — get auto-share status */
  getAutoShareStatus: async (groupId) => {
    const response = await apiClient.get(
      `/groups/groups/${groupId}/auto_share_status/`
    );
    return response.data;
  },
  };

// ── Invitations ───────────────────────────────────────────────────────────────

export const invitationsAPI = {
  /** GET /api/groups/invitations/?status=pending */
  getInvitations: async (params = {}) => {
    const response = await apiClient.get('/groups/invitations/', { params });
    return response.data;
  },

  /** POST /api/groups/invitations/accept/ */
  accept: async (invitationId) => {
    const response = await apiClient.post('/groups/invitations/accept/', {
      invitation_id: invitationId,
    });
    return response.data;
  },

  /** POST /api/groups/invitations/decline/ */
  decline: async (invitationId) => {
    const response = await apiClient.post('/groups/invitations/decline/', {
      invitation_id: invitationId,
    });
    return response.data;
  },

  /** POST /api/groups/invitations/{id}/cancel/ */
  cancel: async (invitationId) => {
    const response = await apiClient.post(`/groups/invitations/${invitationId}/cancel/`);
    return response.data;
  },

  /** POST /api/groups/invitations/{id}/resend/ */
  resend: async (invitationId) => {
    const response = await apiClient.post(`/groups/invitations/${invitationId}/resend/`);
    return response.data;
  },
};

// ── Notifications ─────────────────────────────────────────────────────────────

export const notificationsAPI = {
  /** GET /api/groups/notifications/ */
  getAll: async (params = {}) => {
    const response = await apiClient.get('/groups/notifications/', { params });
    return response.data;
  },

  /** GET /api/groups/notifications/unread_count/ */
  getUnreadCount: async () => {
    const response = await apiClient.get('/groups/notifications/unread_count/');
    return response.data;
  },

  /** POST /api/groups/notifications/{id}/read/ */
  markRead: async (notificationId) => {
    const response = await apiClient.post(`/groups/notifications/${notificationId}/read/`);
    return response.data;
  },

  /** POST /api/groups/notifications/read_all/ */
  markAllRead: async () => {
    const response = await apiClient.post('/groups/notifications/read_all/');
    return response.data;
  },

  /** DELETE /api/groups/notifications/{id}/ */
  delete: async (notificationId) => {
    await apiClient.delete(`/groups/notifications/${notificationId}/`);
  },
};

// ── User search ───────────────────────────────────────────────────────────────

export const userSearchAPI = {
  /** GET /api/groups/users/search/?q=query */
  search: async (query) => {
    if (!query || query.trim().length < 2) return [];
    const response = await apiClient.get('/groups/users/search/', { params: { q: query } });
    return response.data?.results ?? response.data;
  },
};

// ── Profile ───────────────────────────────────────────────────────────────────

export const profileAPI = {
  /** GET /api/auth/profile/ */
  getProfile: async () => {
    const response = await apiClient.get('/auth/profile/');
    return response.data;
  },

  /** PUT /api/auth/profile/ */
  updateProfile: async (data) => {
    const response = await apiClient.put('/auth/profile/', data);
    return response.data;
  },

  /** GET /api/auth/profile/settings/ */
  getSettings: async () => {
    const response = await apiClient.get('/auth/profile/settings/');
    return response.data;
  },

  /** PATCH /api/auth/profile/settings/ */
  updateSettings: async (data) => {
    const response = await apiClient.patch('/auth/profile/settings/', data);
    return response.data;
  },

  /** POST /api/auth/change-password/ */
  changePassword: async (oldPassword, newPassword, newPasswordConfirm) => {
    const response = await apiClient.post('/auth/change-password/', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    });
    return response.data;
  },
};
